import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import NoteEditor from './NoteEditor';
import './VocabularyPage.css';

const v = key => t(`vocabulary.${key}`);
export default function VocabularyNoteEditorPage() {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [note, setNote] = useState(null);
  const [cover, setCover] = useState(null);
  const [loading, setLoading] = useState(Boolean(noteId));
  const [error, setError] = useState('');
  const editorRef = useRef(null);
  const alive = useRef(false);
  const loadSequence = useRef(0);
  const load = useCallback(async () => {
    setError(''); setLoading(true);
    const sequence = ++loadSequence.current;
    try { const next = await apiClient.getVocabularyNote(noteId); if (alive.current && sequence === loadSequence.current) setNote(next); }
    catch (err) { if (alive.current && sequence === loadSequence.current) setError(err.message || v('failed')); }
    finally { if (alive.current && sequence === loadSequence.current) setLoading(false); }
  }, [noteId]);
  useEffect(() => {
    alive.current = true; setNote(null);
    if (noteId) void load(); else setLoading(false);
    return () => { alive.current = false; };
  }, [load, noteId]);
  useEffect(() => {
    let canceled = false; let objectUrl; setCover(null);
    if (note?.coverUrl) apiClient.fetchVocabularyCover(noteId).then(blob => {
      if (canceled) return;
      objectUrl = URL.createObjectURL(blob); setCover(objectUrl);
    }).catch(() => { /* Editing remains possible when the cover is unavailable. */ });
    return () => { canceled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [noteId, note?.coverUrl]);
  const back = () => navigate(noteId ? `/vocabulary/notes/${noteId}` : '/vocabulary');
  return <main className="vocabulary-page vocabulary-note-editor-page">
    <header className="app-page-header"><button type="button" className="btn-back-inline" aria-label={v('backBooks')} onClick={() => editorRef.current ? editorRef.current.requestCancel() : back()}><ArrowLeft size={22} /></button><div className="app-page-header-copy"><h1>{v(noteId ? 'editorEditTitle' : 'editorCreateTitle')}</h1><p>{v('editorDescription')}</p></div></header>
    {loading && <p role="status">{v('loading')}</p>}
    {error && <div className="vocabulary-error" role="alert"><p>{error}</p><button type="button" onClick={load}>{v('retry')}</button></div>}
    {!loading && (!noteId || note) && <NoteEditor key={noteId || 'new'} ref={editorRef} initialEditingCardId={location.state?.editCardId} initialNote={note || { studyKind: 'vocabulary', questionLocale: 'ko-KR', learningLocale: 'en-US', isEnabled: true }} studyKind="vocabulary" mode={noteId ? 'edit' : 'create'} destination="personal" currentCoverPreview={cover}
      onCancel={back} onSave={payload => noteId ? apiClient.updateNoteContent(noteId, { ...payload, studyKind: 'vocabulary' }) : apiClient.composeNote({ ...payload, studyKind: 'vocabulary' })}
      onSaved={result => navigate(`/vocabulary/notes/${noteId || result.note?.noteId || result.noteId || result.note?.id || result.id}`, { replace: true })} />}
  </main>;
}
